import React from 'react';
import { makeStyles, Typography } from '@material-ui/core';

import { theme } from 'config/theme';

const PANEL_WIDTH = 304;
const PANEL_GUTTER = 16;
const TOOLBAR_HEIGHT = 76;
const CANVAS_MIN_HEIGHT = 420;
const COLUMN_GAP = 12;
const DIVIDER = `1px solid ${theme.palette.grey['300']}`;

const useStyles = makeStyles({
    root: {
        flex: '1 1 0',
        minHeight: TOOLBAR_HEIGHT + COLUMN_GAP + CANVAS_MIN_HEIGHT,
        display: 'flex',
    },
    canvasColumn: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: COLUMN_GAP,
    },
    toolbar: {
        height: TOOLBAR_HEIGHT,
        flexShrink: 0,
        minWidth: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'space-between',
        gap: 8,
    },
    heading: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.palette.text.primary,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
    },
    canvas: {
        flex: 1,
        minHeight: 0,
        position: 'relative' as const,
        borderRadius: 4,
        overflow: 'hidden',
        border: DIVIDER,
    },
    // The SVG sizes itself by percentage; an absolute box gives it a definite height in every browser.
    canvasFill: {
        position: 'absolute' as const,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },
    // Shrink-to-fit in the corner the zoom controls do not use, and click-through outside the message itself.
    notice: {
        position: 'absolute' as const,
        top: 12,
        right: 12,
        maxWidth: 'calc(100% - 24px)',
        pointerEvents: 'none' as const,
        '& > *': {
            pointerEvents: 'auto' as const,
        },
    },
    panel: {
        width: PANEL_WIDTH,
        flexShrink: 0,
        boxSizing: 'border-box' as const,
        overflowY: 'auto' as const,
    },
    panelRight: {
        marginLeft: PANEL_GUTTER,
        paddingLeft: PANEL_GUTTER,
        borderLeft: DIVIDER,
    },
    panelLeft: {
        marginRight: PANEL_GUTTER,
        paddingRight: PANEL_GUTTER,
        borderRight: DIVIDER,
    },
});

/** The one heading of a step's toolbar slot. */
export function CanvasStepHeading({ children }: { children: React.ReactNode }) {
    const classes = useStyles();
    return <Typography className={classes.heading}>{children}</Typography>;
}

interface CanvasStepLayoutProps {
    toolbar: React.ReactNode;
    panel: React.ReactNode;
    panelSide: 'left' | 'right';
    /** Laid over a canvas corner so a transient message never rescales the map. */
    notice?: React.ReactNode;
    children: React.ReactNode;
}

export default function CanvasStepLayout({
    toolbar,
    panel,
    panelSide,
    notice,
    children,
}: CanvasStepLayoutProps) {
    const classes = useStyles();
    const panelElement = (
        <div
            className={`${classes.panel} ${
                panelSide === 'left' ? classes.panelLeft : classes.panelRight
            }`}
        >
            {panel}
        </div>
    );
    return (
        <div className={classes.root}>
            {panelSide === 'left' && panelElement}
            <div className={classes.canvasColumn}>
                <div className={classes.toolbar}>{toolbar}</div>
                <div className={classes.canvas}>
                    <div className={classes.canvasFill}>{children}</div>
                    {notice && <div className={classes.notice}>{notice}</div>}
                </div>
            </div>
            {panelSide === 'right' && panelElement}
        </div>
    );
}
